import React, { Component } from 'react';
import { graphql, withApollo } from 'react-apollo';
import { compose, mapProps } from 'recompose';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';

import SaveAssessmentButton from 'components/threepp/assessment/Footer/SaveAssessmentButton/SaveAssessmentButton';
import ActiveUsers from 'components/threepp/assessment/Footer/ActiveUsers/ActiveUsers';
import Tooltip from 'components/common/Tooltip/Tooltip';
import Loader from 'components/common/Loader/Spinner/Spinner';
import Button from 'components/common/Buttons/Button';
import { showErrorMessage } from 'components/common/Alert/Alert';
import {
  getIdFromUrlParams,
  isAssessmentSaving,
  isAssessmentReadOnly as isAssessmentReadOnlySelector,
  getDDERequiredFields
} from 'selectors/assessment/selector';
import { hasCurrentUser, getCurrentUser as getCurrentUserSelector } from 'selectors/user/selector';
import { listFieldsState, getCurrentUser, getInvalidFields } from 'graphql/operations';
import { toFormattedLocalDate, toUTC } from 'utils/dateTime/converter';
import SubmitModal from 'components/threepp/assessment/Footer/SubmitModal';
import { NETWORK_ONLY } from 'graphql/fetchPolicies';
import { RequiredFieldsValidationModal } from './RequiredFieldsValidationModal';
import { InputFieldsValidationModal } from './InputFieldsValidationModal';

import './Footer.scss';

const styles = () => ({
  panelExpanded: {
    marginRight: '24.6vw'
  },
  panelCollapsed: {
    marginRight: '12.1vw'
  },
  submitBtn: {
    width: 'max-content',
    height: '28px'
  },
  actionButtons: {
    display: 'flex'
  }
});

export class AssessmentFooter extends Component {
  state = {
    localAssessmentSaveTime: null,
    showSubmitModal: false,
    showValidationErrorModal: false
  };

  static defaultProps = {
    showRefreshPendingMsg: false
  };

  static propTypes = {
    isOwner: PropTypes.bool.isRequired,
    isSaving: PropTypes.bool.isRequired,
    t: PropTypes.func.isRequired,
    // TODO: Fix this...
    // eslint-disable-next-line react/forbid-prop-types
    assessment: PropTypes.object.isRequired,
    showRefreshPendingMsg: PropTypes.bool
  };

  onAssessmentSaved = timestamp => {
    this.setState({ localAssessmentSaveTime: toUTC(timestamp) });
  };

  getLastSavedText = () => {
    let timestamp = null;
    const {
      t,
      assessment: { lastUpdated }
    } = this.props;
    const { localAssessmentSaveTime } = this.state;
    if (lastUpdated && localAssessmentSaveTime) {
      timestamp = moment(lastUpdated).isAfter(localAssessmentSaveTime) ? lastUpdated : localAssessmentSaveTime;
    } else if (lastUpdated) {
      timestamp = lastUpdated;
    } else if (localAssessmentSaveTime) {
      timestamp = localAssessmentSaveTime;
    }
    const formattedDate = toFormattedLocalDate(timestamp, 'MMMM DD, yyyy');
    const formattedTime = toFormattedLocalDate(timestamp, 'h:mm A');
    return timestamp && `${t('assessment.lastSaved')} ${formattedDate} ${t('common.dateTime.at')} ${formattedTime}`;
  };

  toggleSubmitModal = () => {
    this.setState(prevState => ({ showSubmitModal: !prevState.showSubmitModal }));
  };

  toggleValidationErrorModal = () => {
    this.setState(prevState => ({ showValidationErrorModal: !prevState.showValidationErrorModal }));
  };

  handleSubmit = async () => {
    const { assessment, t, apolloClient } = this.props;

    try {
      const res = await apolloClient.query({
        query: getInvalidFields,
        variables: { assessmentId: assessment.id },
        fetchPolicy: NETWORK_ONLY
      });
      const { hasValidationErrors } = res.data.getInvalidFields;

      if (hasValidationErrors) {
        this.setState({ showValidationErrorModal: true });
      } else {
        this.setState({ showSubmitModal: true });
      }
    } catch (err) {
      showErrorMessage(t('assessment.validations.unexpected'));
    }
  };

  render() {
    const {
      t,
      assessment,
      isSaving,
      isOwner,
      isAssessmentReadOnly,
      attachmentUploadCount,
      classes,
      DDERequiredFields,
      panelExpanded,
      isMobile,
      showRefreshPendingMsg,
      triggerGuidedTour
    } = this.props;

    const { showSubmitModal, showValidationErrorModal } = this.state;

    const hasUnansweredDDERequiredFields = DDERequiredFields?.filter(field => !field.answered).length > 0;
    let submitDisabled = false;
    let disabledReason = '';
    if (isAssessmentReadOnly) {
      submitDisabled = true;
      disabledReason = t('assessment.submit.assessmentReadOnly');
    } else if (attachmentUploadCount > 0) {
      submitDisabled = true;
      disabledReason = t('assessment.submit.uploadAttachment');
    } else if (!isOwner) {
      submitDisabled = true;
      disabledReason = t('assessment.submit.notOwner');
    } else if (assessment.metadata.unansweredRequiredFields || hasUnansweredDDERequiredFields) {
      submitDisabled = true;
      disabledReason = t('assessment.submit.hasPendingRequiredFields');
    }
    return (
      <div className={`footer-actions ${isAssessmentReadOnly ? 'read-only' : ''}`}>
        {!isAssessmentReadOnly && <ActiveUsers />}
        {triggerGuidedTour && (
          <div className={!isMobile ? 'guided-tour-submit-assessment-wrapper' : 'guided-tour-mobile-submit-assessment-wrapper'}>
            <div className={!isMobile ? 'guided-tour-submit-assessment' : 'guided-tour-mobile-submit-assessment'} />
          </div>
        )}
        <div
          className={`${(() => {
            if (!isMobile) {
              return panelExpanded ? classes.panelExpanded : classes.panelCollapsed;
            }
            return '';
          })()}`}
        >
          <div className="actions-block">
            <div className="assessment-status">
              {isSaving ? (
                <div className="saving">
                  <div className="text">{t('assessment.saving')}</div>
                  <Loader size="sm" />
                </div>
              ) : (
                this.getLastSavedText()
              )}
            </div>
            <div className={classes.actionButtons}>
              {!isAssessmentReadOnly && <SaveAssessmentButton onAssessmentSaved={this.onAssessmentSaved} assessment={assessment} isOwner={isOwner} />}
              {isOwner && (
                <Tooltip title={disabledReason}>
                  {/* Tooltip requires a non-disabled child */}
                  <div>
                    {showRefreshPendingMsg ? (
                      <RequiredFieldsValidationModal toggle={this.toggleSubmitModal} isOpen={showSubmitModal} />
                    ) : (
                      <>
                        <InputFieldsValidationModal toggle={this.toggleValidationErrorModal} isOpen={showValidationErrorModal} />
                        <SubmitModal toggle={this.toggleSubmitModal} isOpen={showSubmitModal} />
                      </>
                    )}
                    <Button
                      testId="assessment-submit-button"
                      className={classes.submitBtn}
                      disabled={submitDisabled}
                      buttonStyle="primary"
                      onClick={this.handleSubmit}
                    >
                      {t('assessment.completeAssessment')}
                    </Button>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export const AssessmentFooterHOC = compose(
  withApollo,
  withRouter,
  withTranslation(),
  withStyles(styles(), { name: 'classes' }),
  graphql(listFieldsState, { name: 'listFieldsState' }),
  graphql(getCurrentUser, { name: 'getCurrentUser' }),
  mapProps(props => {
    const { assessment, classes, panelExpanded, isMobile } = props;
    return {
      t: props.t,
      apolloClient: props.client,
      assessment,
      classes,
      panelExpanded,
      isMobile,
      assessmentId: getIdFromUrlParams(props),
      actions: props.actions,
      isOwner: hasCurrentUser({ ...props, users: assessment.owners }),
      isSaving: isAssessmentSaving(props),
      currentUser: getCurrentUserSelector(props),
      isAssessmentReadOnly: isAssessmentReadOnlySelector(props),
      attachmentUploadCount: props.attachmentUploadCount,
      DDERequiredFields: getDDERequiredFields(props),
      showRefreshPendingMsg: props.showRefreshPendingMsg,
      triggerGuidedTour: props.triggerGuidedTour
    };
  })
);

export default AssessmentFooterHOC(AssessmentFooter);
